"use client";

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import { DownloadSimpleIcon, FileCloudIcon, FunnelIcon, ListPlusIcon, NewspaperClippingIcon, CheckIcon, CaretDoubleDownIcon, CaretDoubleUpIcon, FilesIcon, NotePencilIcon, ClipboardTextIcon, CameraIcon, CopySimpleIcon, TrashIcon, Article, FrameCornersIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Tooltip } from "@heroui/tooltip";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import MediaDisplay from "./ui/mediaDisplay";
import type { MediaShareEntry } from "./mediaSourceShare";
import { mediaShareEntryToSourceObject } from "./mediaSourceShare";
import type { ResearchNote } from "./research-notes";
import type { CopyHistoryEntry } from "./recently-copied";
import { type ToolsSharedCaptureEntry } from "./tools-shared-case-data";
import { webpEntryToDataUrl } from "./tools-captures";
import { useToolsDrawer } from "./tools-drawer";
import type { MediaObject, SourceObject, Source } from "@/shared/types";
import { handleDownload as handleDownloadSource, isRestrictedFileType } from "@/shared/functions";
import type { EventFromChat } from "./answer-item";
dayjs.extend(relativeTime);
const MarkdownEditor = dynamic(() => import("./ui/markdownEditor"), {
  ssr: false
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FavoriteItemType = "source" | "note" | "copy" | "capture";
export interface FavoriteItem {
  id: string;
  type: FavoriteItemType;
  timestamp: number;
  data: MediaShareEntry | ResearchNote | CopyHistoryEntry | ToolsSharedCaptureEntry;
}
export type DateRange = "all" | "24h" | "7d" | "30d";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export function mergeAllFavorites(entries: MediaShareEntry[], notes: ResearchNote[], copyHistory: CopyHistoryEntry[], captures: ToolsSharedCaptureEntry[]): FavoriteItem[] {
  const items: FavoriteItem[] = [];
  for (const e of entries) {
    items.push({
      id: e.id,
      type: "source",
      timestamp: e.timestamp,
      data: e
    });
  }
  for (const n of notes) {
    items.push({
      id: n.id,
      type: "note",
      timestamp: n.updatedAt || n.createdAt,
      data: n
    });
  }
  for (const c of copyHistory) {
    items.push({
      id: c.id,
      type: "copy",
      timestamp: c.timestamp,
      data: c
    });
  }
  for (const cap of captures) {
    items.push({
      id: cap.id,
      type: "capture",
      timestamp: cap.createdAt,
      data: cap
    });
  }
  items.sort((a, b) => b.timestamp - a.timestamp);
  return items;
}
export function filterFavorites(items: FavoriteItem[], selectedTypes: Set<FavoriteItemType>, dateRange: DateRange): FavoriteItem[] {
  const now = Date.now();
  const cutoff = dateRange === "all" ? 0 : dateRange === "24h" ? now - 86_400_000 : dateRange === "7d" ? now - 604_800_000 : now - 2_592_000_000;
  return items.filter(item => selectedTypes.has(item.type)).filter(item => item.timestamp >= cutoff);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PREVIEW_MAX_CHARS = 80;
function previewText(text: string, max = PREVIEW_MAX_CHARS): string {
  const t = text.trim();
  return t.length <= max ? t : t.slice(0, max) + "…";
}
function sourceLabel(e: MediaShareEntry): string {
  const p = e.truncatedFilePath;
  if (p && p.trim()) return p;
  const u = e.mediaUrl || e.mediaSrc || "";
  if (!u) return "Unknown";
  const parts = u.split("/");
  return parts[parts.length - 1] || u;
}
function noteTitle(note: ResearchNote): string {
  const t = (note.title || "").trim();
  return t || "Untitled note";
}
const TYPE_META: Record<FavoriteItemType, {
  Icon: React.ComponentType<any>;
  color: string;
}> = {
  source: {
    Icon: FilesIcon,
    color: "text-blue-400"
  },
  note: {
    Icon: NotePencilIcon,
    color: "text-amber-400"
  },
  copy: {
    Icon: ClipboardTextIcon,
    color: "text-green-400"
  },
  capture: {
    Icon: CameraIcon,
    color: "text-purple-400"
  }
};

/** Filter chip labels (plural where it reads naturally). */
const FILTER_TAB_LABEL: Record<FavoriteItemType, string> = {
  source: "Sources",
  note: "Notes",
  copy: "Copied",
  capture: "Captures"
};

/** Card row type title (singular). */
const CARD_TYPE_TITLE: Record<FavoriteItemType, string> = {
  source: "Source",
  note: "Note",
  copy: "Copy",
  capture: "Capture"
};
function itemPreview(item: FavoriteItem): string {
  switch (item.type) {
    case "source":
      return previewText(sourceLabel(item.data as MediaShareEntry));
    case "note":
      return previewText(noteTitle(item.data as ResearchNote));
    case "copy":
      return previewText((item.data as CopyHistoryEntry).text);
    case "capture":
      {
        const cap = item.data as ToolsSharedCaptureEntry;
        return `Capture · ${(cap.webpBase64.length / 1024).toFixed(0)} KB`;
      }
  }
}

// ---------------------------------------------------------------------------
// FilterBar
// ---------------------------------------------------------------------------

function FilterBar({
  selectedTypes,
  setSelectedTypes,
  dateRange,
  setDateRange,
  availableTypes
}: {
  selectedTypes: Set<FavoriteItemType>;
  setSelectedTypes: React.Dispatch<React.SetStateAction<Set<FavoriteItemType>>>;
  dateRange: DateRange;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  availableTypes: Set<FavoriteItemType>;
}) {
  const toggleType = (t: FavoriteItemType) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(t)) {
        if (next.size > 1) next.delete(t);
      } else {
        next.add(t);
      }
      return next;
    });
  };
  const isFiltered = selectedTypes.size < availableTypes.size || dateRange !== "all";
  return <div className="flex flex-col gap-4 px-3 pt-2 pb-6 border-b border-white/10 shrink-0" data-sentry-component="FilterBar" data-sentry-source-file="favorites-list.tsx">
      <div className="flex items-center gap-x-1.5 gap-y-2 flex-wrap">
        <span className="text-white/50 text-xs font-medium mr-0.5">Viewing:</span>
        {(Object.keys(TYPE_META) as FavoriteItemType[]).filter(t => availableTypes.has(t)).map(t => {
        const {
          Icon,
          color
        } = TYPE_META[t];
        const active = selectedTypes.has(t);
        return <button key={t} type="button" onClick={() => toggleType(t)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer border ${active ? "bg-white/10 border-white/20 text-white" : "bg-transparent border-white/5 text-white/40 hover:text-white/60 hover:border-white/10"}`}>
              <Icon size={15} weight="duotone" className={active ? color : "text-white/30"} />
              {FILTER_TAB_LABEL[t]}
            </button>;
      })}
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-white/50 text-xs font-medium">Saved:</span>
          <select value={dateRange} onChange={e => setDateRange(e.target.value as DateRange)} className="bg-white/5 border border-white/10 text-white/80 text-xs rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-white/30 cursor-pointer">
            <option value="all">All time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </div>
      {isFiltered && <button type="button" onClick={() => {
      setSelectedTypes(new Set(availableTypes));
      setDateRange("all");
    }} className="text-xs text-white/50 hover:text-white/80 cursor-pointer self-start">
          Clear filters
        </button>}
    </div>;
}

// ---------------------------------------------------------------------------
// FavoriteCard
// ---------------------------------------------------------------------------

function FavoriteCard({
  item,
  isSelected,
  onSelect
}: {
  item: FavoriteItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const {
    Icon,
    color
  } = TYPE_META[item.type];
  const cardTitle = CARD_TYPE_TITLE[item.type];
  return <li className={`rounded-lg px-3 pt-3 pb-2 cursor-pointer transition-all group ${isSelected ? "bg-white/10 border border-white/20 shadow-[0_4px_12px_rgba(0,0,0,0.2)]" : "bg-white/5 border border-white/[0.07] hover:bg-white/10 hover:border-white/20 hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)]"}`} onClick={onSelect} data-sentry-component="FavoriteCard" data-sentry-source-file="favorites-list.tsx">
      <div className="flex flex-row items-center gap-1.5 mb-1.5">
        <Icon size={16} weight="duotone" className={color} data-sentry-element="Icon" data-sentry-source-file="favorites-list.tsx" />
        <span className={`text-xs font-medium ${isSelected ? "text-white" : "text-white/70 group-hover:text-white"} transition-colors`}>{cardTitle}</span>
      </div>

      {item.type === "capture" && <div className="rounded border border-white/10 overflow-hidden bg-black/40 w-full aspect-video flex items-center justify-center mb-2">
          <img src={webpEntryToDataUrl(item.data as ToolsSharedCaptureEntry)} alt="" className="max-w-full max-h-[80px] w-auto h-auto object-contain" />
        </div>}

      <p className={`text-sm font-medium line-clamp-2 break-words mb-1 transition-colors ${isSelected ? "text-blue-300" : "text-white group-hover:text-blue-300"}`}>
        {itemPreview(item)}
      </p>

      <p className="text-white/50 text-[11px]">
        {dayjs(item.timestamp).fromNow()}
      </p>
    </li>;
}

// ---------------------------------------------------------------------------
// Detail Views
// ---------------------------------------------------------------------------

function SourceDetailView({
  entry,
  caseId,
  onRemove,
  onClose,
  onAddToTimeline,
  addSharedSourceToEvent,
  eventSharedSourceAddReady = false
}: {
  entry: MediaShareEntry;
  caseId?: string;
  onRemove: () => void;
  onClose?: () => void;
  onAddToTimeline?: (event: EventFromChat) => void;
  addSharedSourceToEvent?: (source: SourceObject) => boolean;
  eventSharedSourceAddReady?: boolean;
}) {
  const [addedToScopeId, setAddedToScopeId] = useState<string | null>(null);
  const [copiedPathId, setCopiedPathId] = useState<string | null>(null);
  const [createdEventId, setCreatedEventId] = useState<string | null>(null);
  const [justAddedToEventSources, setJustAddedToEventSources] = useState(false);
  const [isCitationExpanded, setIsCitationExpanded] = useState(true);
  useEffect(() => {
    setIsCitationExpanded(true);
  }, [entry.id]);
  const entryToSource = useCallback((e: MediaShareEntry): Source => ({
    sourceFilePath: e.mediaUrl || e.mediaSrc,
    sourceUrl: e.mediaUrl || e.mediaSrc,
    truncatedFilePath: e.truncatedFilePath,
    sourceContext: e.mediaContext,
    sourceInfo: e.sourceInfo,
    page: e.page ?? e.mediaContext?.pageNumber?.toString(),
    content: e.content,
    location: e.location
  }), []);
  const handleDownload = useCallback(() => handleDownloadSource(entryToSource(entry)), [entry, entryToSource]);
  const handleAddToScope = useCallback(() => {
    const path = entry.mediaUrl || entry.mediaSrc || entry.truncatedFilePath || "";
    if (!path || !caseId) return;
    const key = `${caseId}-scopesObject`;
    const current = JSON.parse(sessionStorage.getItem(key) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }');
    current.files = Array.from(new Set([...current.files, path]));
    sessionStorage.setItem(key, JSON.stringify(current));
    window.dispatchEvent(new CustomEvent("scopes-updated", {
      detail: {
        caseId
      }
    }));
    setAddedToScopeId(entry.id);
    setTimeout(() => setAddedToScopeId(null), 1500);
  }, [entry, caseId]);
  const handleCopyPath = useCallback(async () => {
    const path = entry.truncatedFilePath || entry.mediaUrl || entry.mediaSrc || "";
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPathId(entry.id);
      setTimeout(() => setCopiedPathId(null), 1500);
    } catch {/* ignore */}
  }, [entry]);
  const handleCreateEvent = useCallback(() => {
    if (!onAddToTimeline || !caseId) return;
    const source = entryToSource(entry);
    const event: EventFromChat = {
      answerText: "FROM_SOURCE",
      sources: [source],
      media: [entry as MediaObject],
      autogen: false,
      epochTimeSecs: Math.floor(Date.now() / 1000)
    };
    onAddToTimeline(event);
    setCreatedEventId(entry.id);
    setTimeout(() => setCreatedEventId(null), 1500);
  }, [entry, onAddToTimeline, caseId, entryToSource]);
  const handleAddToEventSources = useCallback(() => {
    if (!addSharedSourceToEvent || !eventSharedSourceAddReady) return;
    const source = mediaShareEntryToSourceObject(entry);
    if (!source) return;
    const ok = addSharedSourceToEvent(source);
    if (!ok) return;
    setJustAddedToEventSources(true);
    setTimeout(() => setJustAddedToEventSources(false), 1500);
  }, [entry, addSharedSourceToEvent, eventSharedSourceAddReady]);
  return <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-3 py-1" data-sentry-component="SourceDetailView" data-sentry-source-file="favorites-list.tsx">
      <div className="flex flex-row gap-1 shrink-0 pb-2 border-b border-white/20 mb-2">
        <Tooltip content="Download source" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${isRestrictedFileType(entry) ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={handleDownload} disabled={isRestrictedFileType(entry)} aria-label="Download source">
            <DownloadSimpleIcon size={20} data-sentry-element="DownloadSimpleIcon" data-sentry-source-file="favorites-list.tsx" />
          </button>
        </Tooltip>
        <Tooltip content="Add source to scope" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${addedToScopeId === entry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={handleAddToScope} disabled={!caseId} aria-label="Add source to scope">
            {addedToScopeId === entry.id ? <CheckIcon size={20} /> : <FunnelIcon size={20} />}
          </button>
        </Tooltip>
        <Tooltip content={copiedPathId === entry.id ? "Copied" : "Copy source file path"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${copiedPathId === entry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={handleCopyPath} aria-label={copiedPathId === entry.id ? "Copied" : "Copy source file path"}>
            {copiedPathId === entry.id ? <CheckIcon size={20} /> : <FileCloudIcon size={20} />}
          </button>
        </Tooltip>
        <Tooltip content={createdEventId === entry.id ? "Event created" : "Create timeline event from source"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${createdEventId === entry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={handleCreateEvent} disabled={!onAddToTimeline || !caseId} aria-label={createdEventId === entry.id ? "Event created" : "Create timeline event from source"}>
            {createdEventId === entry.id ? <CheckIcon size={20} /> : <ListPlusIcon size={20} />}
          </button>
        </Tooltip>
        <Tooltip content={eventSharedSourceAddReady ? "Add this source to Selected Sources in the open event editor" : "Open an event for editing to add this source"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[260px]",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button type="button" className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${justAddedToEventSources ? "bg-brand-blue text-white" : eventSharedSourceAddReady ? "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" : "text-white/20 cursor-not-allowed"}`} onClick={handleAddToEventSources} disabled={!eventSharedSourceAddReady} aria-label="Add source to event selected sources">
            {justAddedToEventSources ? <CheckIcon size={20} /> : <NewspaperClippingIcon size={20} weight="duotone" />}
          </button>
        </Tooltip>
        <Tooltip content="Remove Favorite" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className="h-[28px] w-[28px] flex items-center justify-center rounded p-1 text-white/70 hover:text-white hover:bg-red-500/20 cursor-pointer" onClick={onRemove} aria-label="Remove Favorite">
            <TrashIcon size={20} weight="duotone" data-sentry-element="TrashIcon" data-sentry-source-file="favorites-list.tsx" />
          </button>
        </Tooltip>
        {onClose && <>
            <div className="flex-1" />
            <Tooltip content="Close Favorite" placement="bottom" delay={0} classNames={{
          content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
          base: "before:bg-tooltip-bg"
        }}>
              <button type="button" onClick={onClose} className="h-[28px] w-[28px] flex items-center justify-center rounded p-1 text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" aria-label="Close Favorite">
                <XIcon size={20} weight="bold" />
              </button>
            </Tooltip>
          </>}
      </div>

      {(entry.page && !isNaN(parseInt(entry.page)) || entry.content && entry.content.trim() !== "" && !entry.location?.includes("/bkb/slow/transcribe/") && !entry.location?.endsWith(".transcript.txt")) && <div className="flex flex-row items-top justify-start w-full shrink-0 pb-2 border-b border-white/20 mb-2">
          {entry.content && entry.content.trim() !== "" && !entry.location?.includes("/bkb/slow/transcribe/") && !entry.location?.endsWith(".transcript.txt") && <div className="cursor-pointer w-[20px] h-[20px] pl-1 pr-2" onClick={() => setIsCitationExpanded(p => !p)}>
              {isCitationExpanded ? <CaretDoubleUpIcon size={20} /> : <CaretDoubleDownIcon size={20} />}
            </div>}
          <div className={`box-border flex flex-col gap-0 flex-1 min-w-0 ${entry.content && entry.content.trim() !== "" && !entry.location?.includes("/bkb/slow/transcribe/") && !entry.location?.endsWith(".transcript.txt") && !isCitationExpanded ? "h-[21px] max-h-[21px] overflow-hidden opacity-0 transition-opacity duration-300 ease-in-out" : ""}`}>
            {entry.page && !isNaN(parseInt(entry.page)) && <div className="text-white font-normal text-base mb-2">Page {entry.page}</div>}
            {entry.content && entry.content.trim() !== "" && !entry.location?.includes("/bkb/slow/transcribe/") && !entry.location?.endsWith(".transcript.txt") && <div className="flex flex-col gap-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            p: ({
              children
            }) => <span className="mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal">{children}</span>,
            h2: ({
              children
            }) => <span className="mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal">{children}</span>,
            li: ({
              children
            }) => <li className="mb-2 border-l-4 border-l-brand-blue/25 border-solid pl-2 leading-normal">{children}</li>,
            a: ({
              children
            }) => <a className="text-brand-blue-light italic">{children}</a>
          }}>
                  {entry.content?.replace("```json ", "")}
                </ReactMarkdown>
              </div>}
          </div>
        </div>}

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar2">
        <MediaDisplay mediaItem={{
        ...entry,
        mediaContext: {
          ...entry.mediaContext,
          htmlChunk: entry.content ? entry.content.match(/page \d+/i)?.[0] || "" : "",
          pageNumber: entry.page ? parseInt(entry.page, 10) : entry.mediaContext?.pageNumber ?? 1
        }
      } as MediaObject} sourcesExpanded={false} isMediaShareDrawer={true} data-sentry-element="MediaDisplay" data-sentry-source-file="favorites-list.tsx" />
      </div>
    </div>;
}
function NoteDetailView({
  note,
  onUpdateNote,
  onDeleteNote,
  onClose
}: {
  note: ResearchNote;
  onUpdateNote: (id: string, updates: {
    title?: string;
    content?: string;
  }) => void;
  onDeleteNote: (id: string) => void;
  onClose?: () => void;
}) {
  return <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-3 py-1" data-sentry-component="NoteDetailView" data-sentry-source-file="favorites-list.tsx">
      <div className="flex flex-row items-center gap-2 shrink-0 mb-2">
        <input type="text" value={note.title} onChange={e => onUpdateNote(note.id, {
        title: e.target.value
      })} onBlur={e => {
        if (!e.target.value.trim()) onUpdateNote(note.id, {
          title: "Untitled note"
        });
      }} placeholder="Note title" className="flex-1 px-2 py-1.5 text-base font-medium text-white bg-white/5 border border-white/10 rounded focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/20 placeholder-white/40" aria-label="Note title" />
        <Tooltip content="Remove Favorite" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button type="button" onClick={() => onDeleteNote(note.id)} className="flex items-center justify-center p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10 cursor-pointer" aria-label="Remove Favorite">
            <TrashIcon size={18} weight="duotone" data-sentry-element="TrashIcon" data-sentry-source-file="favorites-list.tsx" />
          </button>
        </Tooltip>
        {onClose && <Tooltip content="Close Favorite" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }}>
            <button type="button" onClick={onClose} className="flex items-center justify-center p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10 cursor-pointer" aria-label="Close Favorite">
              <XIcon size={18} weight="bold" />
            </button>
          </Tooltip>}
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <React.Suspense fallback={<div className="text-white/60 text-sm">Loading editor...</div>} data-sentry-element="unknown" data-sentry-source-file="favorites-list.tsx">
          <MarkdownEditor key={note.id} id={`research-note-${note.id}`} markdown={note.content} onChange={content => onUpdateNote(note.id, {
          content
        })} debounceMs={300} data-sentry-element="MarkdownEditor" data-sentry-source-file="favorites-list.tsx" />
        </React.Suspense>
      </div>
    </div>;
}
function CopyDetailView({
  entry,
  copiedEntryId,
  onCopyEntry,
  onRemove,
  onClose,
  applyTextToEventDescription,
  eventDescriptionApplyReady = false
}: {
  entry: CopyHistoryEntry;
  copiedEntryId: string | null;
  onCopyEntry: (entry: CopyHistoryEntry) => void;
  onRemove: () => void;
  onClose?: () => void;
  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;
}) {
  const [appliedId, setAppliedId] = useState<string | null>(null);
  const handleApply = useCallback(() => {
    if (!applyTextToEventDescription || !eventDescriptionApplyReady) return;
    const ok = applyTextToEventDescription(entry.text);
    if (!ok) return;
    setAppliedId(entry.id);
    setTimeout(() => setAppliedId(null), 1500);
  }, [entry, applyTextToEventDescription, eventDescriptionApplyReady]);
  return <div className="flex flex-col flex-1 min-h-0 overflow-hidden p-3" data-sentry-component="CopyDetailView" data-sentry-source-file="favorites-list.tsx">
      <div className="flex flex-row gap-1 shrink-0 pb-2 border-b border-white/20 mb-2">
        <Tooltip content={copiedEntryId === entry.id ? "Copied" : "Copy to clipboard"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${copiedEntryId === entry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={() => onCopyEntry(entry)} aria-label="Copy to clipboard">
            {copiedEntryId === entry.id ? <CheckIcon size={20} /> : <CopySimpleIcon size={20} weight="duotone" />}
          </button>
        </Tooltip>
        <Tooltip content={eventDescriptionApplyReady ? "Replace the open event's Description with this text" : "Open an event for editing to apply"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[240px]",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button type="button" className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${appliedId === entry.id ? "bg-brand-blue text-white" : eventDescriptionApplyReady ? "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" : "text-white/20 cursor-not-allowed"}`} onClick={handleApply} disabled={!eventDescriptionApplyReady} aria-label="Apply to event description">
            {appliedId === entry.id ? <CheckIcon size={20} /> : <Article size={20} weight="duotone" />}
          </button>
        </Tooltip>
        <Tooltip content="Remove Favorite" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className="h-[28px] w-[28px] flex items-center justify-center rounded p-1 text-white/70 hover:text-white hover:bg-red-500/20 cursor-pointer" onClick={onRemove} aria-label="Remove Favorite">
            <TrashIcon size={20} weight="duotone" data-sentry-element="TrashIcon" data-sentry-source-file="favorites-list.tsx" />
          </button>
        </Tooltip>
        {onClose && <>
            <div className="flex-1" />
            <Tooltip content="Close Favorite" placement="bottom" delay={0} classNames={{
          content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
          base: "before:bg-tooltip-bg"
        }}>
              <button type="button" onClick={onClose} className="h-[28px] w-[28px] flex items-center justify-center rounded p-1 text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" aria-label="Close Favorite">
                <XIcon size={20} weight="bold" />
              </button>
            </Tooltip>
          </>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar2 text-white/90 text-sm break-words leading-relaxed">
        <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} data-sentry-element="ReactMarkdown" data-sentry-source-file="favorites-list.tsx">
          {entry.text}
        </ReactMarkdown>
        <p className="text-white/40 text-[11px] mt-3">{dayjs(entry.timestamp).fromNow()}</p>
      </div>
    </div>;
}
function CaptureDetailView({
  entry,
  onRemoveCapture,
  onClose
}: {
  entry: ToolsSharedCaptureEntry;
  onRemoveCapture: (id: string) => void;
  onClose?: () => void;
}) {
  const toolsDrawer = useToolsDrawer();
  const applyCaptureToEventMedia = toolsDrawer?.applyCaptureToEventMedia;
  const eventMediaCaptureApplyReady = toolsDrawer?.eventMediaCaptureApplyReady ?? false;
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const copyDataUrl = useCallback(() => {
    navigator.clipboard.writeText(webpEntryToDataUrl(entry)).then(() => {
      setCopiedId(entry.id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {/* ignore */});
  }, [entry]);
  const applyToEvent = useCallback(() => {
    if (!applyCaptureToEventMedia || !eventMediaCaptureApplyReady) return;
    const ok = applyCaptureToEventMedia(webpEntryToDataUrl(entry));
    if (!ok) return;
    setApplyingId(entry.id);
    setTimeout(() => setApplyingId(null), 1000);
  }, [entry, applyCaptureToEventMedia, eventMediaCaptureApplyReady]);
  return <div className="flex flex-col flex-1 min-h-0 overflow-hidden p-3" data-sentry-component="CaptureDetailView" data-sentry-source-file="favorites-list.tsx">
      <div className="flex flex-row gap-1 shrink-0 pb-2 border-b border-white/20 mb-2">
        <Tooltip content={copiedId === entry.id ? "Copied" : "Copy data URL"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${copiedId === entry.id ? "bg-brand-blue text-white" : "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer"}`} onClick={copyDataUrl} aria-label="Copy data URL">
            {copiedId === entry.id ? <CheckIcon size={20} /> : <CopySimpleIcon size={20} weight="duotone" />}
          </button>
        </Tooltip>
        <Tooltip content={eventMediaCaptureApplyReady ? "Apply capture to event media" : "Open an event for editing"} placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[240px]",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button type="button" disabled={!eventMediaCaptureApplyReady} className={`h-[28px] w-[28px] flex items-center justify-center rounded p-1 ${applyingId === entry.id ? "bg-brand-blue text-white" : eventMediaCaptureApplyReady ? "text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" : "text-white/20 cursor-not-allowed"}`} onClick={applyToEvent} aria-label="Apply to event media">
            {applyingId === entry.id ? <CheckIcon size={20} /> : <FrameCornersIcon size={20} weight="duotone" />}
          </button>
        </Tooltip>
        <Tooltip content="Remove Favorite" placement="bottom" delay={0} classNames={{
        content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
        base: "before:bg-tooltip-bg"
      }} data-sentry-element="Tooltip" data-sentry-source-file="favorites-list.tsx">
          <button className="h-[28px] w-[28px] flex items-center justify-center rounded p-1 text-white/70 hover:text-white hover:bg-red-500/20 cursor-pointer" onClick={() => onRemoveCapture(entry.id)} aria-label="Remove Favorite">
            <TrashIcon size={20} weight="duotone" data-sentry-element="TrashIcon" data-sentry-source-file="favorites-list.tsx" />
          </button>
        </Tooltip>
        {onClose && <>
            <div className="flex-1" />
            <Tooltip content="Close Favorite" placement="bottom" delay={0} classNames={{
          content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm",
          base: "before:bg-tooltip-bg"
        }}>
              <button type="button" onClick={onClose} className="h-[28px] w-[28px] flex items-center justify-center rounded p-1 text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" aria-label="Close Favorite">
                <XIcon size={20} weight="bold" />
              </button>
            </Tooltip>
          </>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar2 flex items-start justify-center pt-4">
        <img src={webpEntryToDataUrl(entry)} alt="Capture" className="max-w-full max-h-full object-contain rounded border border-white/10" />
      </div>
      <p className="text-white/40 text-[11px] mt-2 shrink-0">
        {dayjs(entry.createdAt).fromNow()} · {(entry.webpBase64.length / 1024).toFixed(0)} KB
      </p>
    </div>;
}

// ---------------------------------------------------------------------------
// Main FavoritesList Component
// ---------------------------------------------------------------------------

export interface FavoritesListProps {
  entries: MediaShareEntry[];
  setEntries: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  persistEntries: (next: MediaShareEntry[]) => void;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
  addSharedSourceToEvent?: (source: SourceObject) => boolean;
  eventSharedSourceAddReady?: boolean;
  notes: ResearchNote[];
  onAddNote: () => string;
  onUpdateNote: (id: string, updates: {
    title?: string;
    content?: string;
  }) => void;
  onDeleteNote: (id: string) => void;
  copyHistory: CopyHistoryEntry[];
  copiedEntryId: string | null;
  onCopyEntry: (entry: CopyHistoryEntry) => void;
  onRemoveCopyEntry: (entryId: string) => void;
  captures: ToolsSharedCaptureEntry[];
  onRemoveCapture: (id: string) => void;
  captureEnabled?: boolean;
  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;
  showFilters: boolean;
  onSelectionChange?: (hasSelection: boolean) => void;
  onCountsChange?: (total: number, filtered: number, isFiltered: boolean) => void;
}
export default function FavoritesList({
  entries,
  setEntries,
  persistEntries,
  caseId,
  onAddToTimeline,
  addSharedSourceToEvent,
  eventSharedSourceAddReady,
  notes,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  copyHistory,
  copiedEntryId,
  onCopyEntry,
  onRemoveCopyEntry,
  captures,
  onRemoveCapture,
  captureEnabled = true,
  applyTextToEventDescription,
  eventDescriptionApplyReady,
  showFilters,
  onSelectionChange,
  onCountsChange
}: FavoritesListProps) {
  const availableTypes = useMemo<Set<FavoriteItemType>>(() => {
    const types: FavoriteItemType[] = ["source", "note", "copy"];
    if (captureEnabled) types.push("capture");
    return new Set(types);
  }, [captureEnabled]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<Set<FavoriteItemType>>(new Set(availableTypes));
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [mobileDetail, setMobileDetail] = useState(false);
  useEffect(() => {
    setSelectedTypes(prev => {
      if (captureEnabled && !prev.has("capture")) {
        return new Set([...prev, "capture"]);
      }
      if (!captureEnabled && prev.has("capture")) {
        const next = new Set(prev);
        next.delete("capture");
        return next;
      }
      return prev;
    });
  }, [captureEnabled]);
  const allItems = useMemo(() => mergeAllFavorites(entries, notes, copyHistory, captureEnabled ? captures : []), [entries, notes, copyHistory, captures, captureEnabled]);
  const filteredItems = useMemo(() => filterFavorites(allItems, selectedTypes, dateRange), [allItems, selectedTypes, dateRange]);
  const selectedItem = useMemo(() => filteredItems.find(i => i.id === selectedId) ?? null, [filteredItems, selectedId]);
  useEffect(() => {
    if (selectedId && !filteredItems.find(i => i.id === selectedId)) {
      setSelectedId(null);
      setMobileDetail(false);
    }
  }, [filteredItems, selectedId]);
  useEffect(() => {
    onSelectionChange?.(selectedId != null);
  }, [selectedId, onSelectionChange]);
  const isFiltered = selectedTypes.size < availableTypes.size || dateRange !== "all";
  useEffect(() => {
    onCountsChange?.(allItems.length, filteredItems.length, isFiltered);
  }, [allItems.length, filteredItems.length, isFiltered, onCountsChange]);
  const handleRemoveItem = useCallback((item: FavoriteItem) => {
    switch (item.type) {
      case "source":
        setEntries(prev => {
          const next = prev.filter(e => e.id !== item.id);
          persistEntries(next);
          return next;
        });
        break;
      case "note":
        onDeleteNote(item.id);
        break;
      case "copy":
        onRemoveCopyEntry(item.id);
        break;
      case "capture":
        onRemoveCapture(item.id);
        break;
    }
    if (selectedId === item.id) {
      setSelectedId(null);
      setMobileDetail(false);
    }
  }, [selectedId, setEntries, persistEntries, onDeleteNote, onRemoveCopyEntry, onRemoveCapture]);
  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    setMobileDetail(true);
  }, []);
  const renderDetail = (onClose?: () => void) => {
    if (!selectedItem) {
      return <div className="flex flex-1 items-center justify-center text-white/50 text-sm">
          Select an item to view
        </div>;
    }
    switch (selectedItem.type) {
      case "source":
        return <SourceDetailView entry={selectedItem.data as MediaShareEntry} caseId={caseId} onRemove={() => handleRemoveItem(selectedItem)} onClose={onClose} onAddToTimeline={onAddToTimeline} addSharedSourceToEvent={addSharedSourceToEvent} eventSharedSourceAddReady={eventSharedSourceAddReady} />;
      case "note":
        return <NoteDetailView note={selectedItem.data as ResearchNote} onUpdateNote={onUpdateNote} onDeleteNote={onDeleteNote} onClose={onClose} />;
      case "copy":
        return <CopyDetailView entry={selectedItem.data as CopyHistoryEntry} copiedEntryId={copiedEntryId} onCopyEntry={onCopyEntry} onRemove={() => handleRemoveItem(selectedItem)} onClose={onClose} applyTextToEventDescription={applyTextToEventDescription} eventDescriptionApplyReady={eventDescriptionApplyReady} />;
      case "capture":
        return <CaptureDetailView entry={selectedItem.data as ToolsSharedCaptureEntry} onRemoveCapture={onRemoveCapture} onClose={onClose} />;
    }
  };
  return <div className="flex flex-1 min-h-0 flex-col overflow-hidden" data-sentry-component="FavoritesList" data-sentry-source-file="favorites-list.tsx">
      {showFilters && <FilterBar selectedTypes={selectedTypes} setSelectedTypes={setSelectedTypes} dateRange={dateRange} setDateRange={setDateRange} availableTypes={availableTypes} />}

      <div className="flex flex-1 min-h-0 flex-row overflow-hidden">
        {/* Master column */}
        <div className={`flex-col overflow-hidden ${mobileDetail ? "hidden" : "flex"} md:!flex ${selectedItem ? "md:w-[280px] md:shrink-0 md:flex-none md:border-r md:border-white/[0.08]" : "w-full md:flex-1"}`}>

          {/* Sub-header */}
        <div className="shrink-0 px-3 py-2 border-b border-white/10 flex items-center gap-2">
          <button type="button" onClick={() => {
            const id = onAddNote();
            setSelectedTypes(prev => {
              if (prev.has("note")) return prev;
              return new Set([...prev, "note"]);
            });
            handleSelect(id);
          }} className="btn-text gap-2 shrink-0" aria-label="New Note">
            <PlusIcon size={16} data-sentry-element="PlusIcon" data-sentry-source-file="favorites-list.tsx" />
            <span>New Note</span>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar2">
          {filteredItems.length === 0 ? <p className="px-3 py-6 text-white/50 text-xs text-center">
              {allItems.length === 0 ? "No favorites yet. Add sources, copy text, or create notes to see them here." : "No items match the current filters."}
            </p> : <>
              <p className="px-3 pt-1.5 pb-0.5 text-white/40 text-xs">
                {isFiltered ? `Viewing ${filteredItems.length} of ${allItems.length} Favorites` : `Viewing ${allItems.length} Favorites`}
              </p>
              <ul className="flex flex-col gap-2 list-none px-2 pb-2">
                {filteredItems.map(item => <FavoriteCard key={item.id} item={item} isSelected={selectedId === item.id} onSelect={() => handleSelect(item.id)} />)}
              </ul>
            </>}
        </div>
      </div>

        {/* Desktop detail panel */}
        {selectedItem && <div className="hidden md:flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">
            {renderDetail(() => {
          setSelectedId(null);
          setMobileDetail(false);
        })}
          </div>}
      </div>

      {/* Mobile full-screen detail modal */}
      {mobileDetail && selectedItem && createPortal(<div className="md:hidden fixed inset-0 z-[5100] flex flex-col bg-gray-900">
          <div className="flex items-center justify-end px-3 pt-3 shrink-0">
            <button type="button" onClick={() => {
          setMobileDetail(false);
          setSelectedId(null);
        }} className="h-[32px] w-[32px] flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/10 cursor-pointer" aria-label="Close detail">
              <XIcon size={22} weight="bold" />
            </button>
          </div>
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {renderDetail()}
          </div>
        </div>, document.body)}
    </div>;
}